import React, { useEffect, useState } from 'react';

// ─── Time Input (handles colon logic) ────────────────────────────────────────

function TimeTextInput({
  inputRef,
  value,
  onChange,
  onCommit,
  onFocus,
  onOpenPicker,
  onRetreatToDate,
  onEnterSegmentMode,
  granularity,
  hourCycle = 12,
  isDisabled,
  isInvalid,
  isRequired,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onCommit: () => void;
  onFocus?: () => void;
  onOpenPicker?: () => void;
  onRetreatToDate?: () => void;
  onEnterSegmentMode?: (enter: () => void) => void;
  granularity: 'hour' | 'minute' | 'second';
  hourCycle?: 12 | 24;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isRequired?: boolean;
}) {
  const [segmentControlled, setSegmentControlled] = useState(false);

  // Register enter-segment-mode fn with parent once on mount.
  // Empty dep array is intentional: we only ever want the initial registration.
  // The parent stores the result in a ref (enterSegmentModeRef), so stale-closure is not an issue.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — see comment above
  useEffect(() => {
    onEnterSegmentMode?.(() => setSegmentControlled(true));
  }, []);
  const suffix = hourCycle === 12 ? ' AM' : '';
  const placeholder =
    granularity === 'second' ? `H:MM:SS${suffix}` : granularity === 'minute' ? `H:MM${suffix}` : `H${suffix}`;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const pos = input.selectionStart ?? 0;

    // Let browser shortcuts through, but clear segment mode
    if ((e.ctrlKey || e.metaKey) && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
      setSegmentControlled(false);
      return;
    }

    if (e.key === 'Enter') {
      onCommit();
    }

    if (e.key === 'Tab') {
      // Shift+Tab goes back to date
      if (e.shiftKey && onRetreatToDate) {
        e.preventDefault();
        onRetreatToDate();
        return;
      }
      onCommit();
    }

    // ArrowLeft at position 0 → go back to date
    if (e.key === 'ArrowLeft' && pos === 0 && onRetreatToDate) {
      e.preventDefault();
      input.setSelectionRange(0, 0);
      onRetreatToDate();
      return;
    }

    // Backspace at position 0 on empty input → go back to date
    if (e.key === 'Backspace' && pos === 0 && !input.value && onRetreatToDate) {
      e.preventDefault();
      onRetreatToDate();
      return;
    }

    // In segment-controlled mode, left/right selects the adjacent segment
    if (segmentControlled && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      const val = input.value;
      // Find segment boundaries (split by : and space)
      const segBoundaries: [number, number][] = [];
      let i = 0;
      while (i < val.length) {
        if (val[i] === ':' || val[i] === ' ') {
          i++;
          continue;
        }
        const start = i;
        while (i < val.length && val[i] !== ':' && val[i] !== ' ') i++;
        segBoundaries.push([start, i]);
      }
      // Find current segment
      const curSeg = segBoundaries.findIndex(([s, e]) => pos >= s && pos <= e);
      const targetSeg = e.key === 'ArrowLeft' ? curSeg - 1 : Math.min(segBoundaries.length - 1, curSeg + 1);
      if (segBoundaries[targetSeg]) {
        const [s, end] = segBoundaries[targetSeg];
        input.setSelectionRange(s, end);
      }
      return;
    }

    // Strip AM/PM suffix for position calculations
    const periodMatch = input.value.match(/\s*(AM|PM)\s*$/i);
    const numericEnd = periodMatch ? input.value.length - periodMatch[0].length : input.value.length;
    const numericPart = input.value.slice(0, numericEnd);

    // Don't let cursor enter the AM/PM suffix area for digit/colon entry
    const effectivePos = Math.min(pos, numericEnd);

    if (e.key === ':') {
      // Skip over an existing colon
      if (numericPart[effectivePos] === ':') {
        e.preventDefault();
        input.setSelectionRange(effectivePos + 1, effectivePos + 1);
        return;
      }

      // Block double colons
      if (effectivePos === 0 || numericPart[effectivePos - 1] === ':') {
        e.preventDefault();
        return;
      }
    }

    // Auto-insert colon when a 2-char segment is full
    if (/^\d$/.test(e.key)) {
      // Keep cursor in the numeric portion
      if (pos > numericEnd) {
        e.preventDefault();
        return;
      }

      const lastColon = numericPart.lastIndexOf(':', effectivePos - 1);
      const segmentLen = effectivePos - (lastColon + 1);
      const colonCount = (numericPart.match(/:/g) || []).length;
      const maxColons = granularity === 'second' ? 2 : granularity === 'minute' ? 1 : 0;

      if (segmentLen >= 2 && colonCount < maxColons) {
        e.preventDefault();
        const suffix = input.value.slice(numericEnd);
        const newNumeric = numericPart.slice(0, effectivePos) + ':' + e.key + numericPart.slice(effectivePos);
        onChange(newNumeric + suffix);
        requestAnimationFrame(() => {
          input.setSelectionRange(effectivePos + 2, effectivePos + 2);
        });
        return;
      }

      if (segmentLen === 1 && numericPart[effectivePos] === ':') {
        requestAnimationFrame(() => {
          input.setSelectionRange(effectivePos + 2, effectivePos + 2);
        });
      }
    }

    // ArrowUp/Down — increment/decrement the segment under the cursor
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      setSegmentControlled(true);
      onOpenPicker?.();
      const delta = e.key === 'ArrowUp' ? 1 : -1;

      // If empty, seed from current time then increment hour
      if (!numericPart.trim()) {
        const now = new Date();
        const h = hourCycle === 12 ? now.getHours() % 12 || 12 : now.getHours();
        const m = now.getMinutes();
        const s = now.getSeconds();
        const period = hourCycle === 12 ? (now.getHours() >= 12 ? ' PM' : ' AM') : '';
        const base =
          granularity === 'second'
            ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
            : granularity === 'minute'
              ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
              : String(h).padStart(2, '0');
        onChange(base + period);
        requestAnimationFrame(() => input.setSelectionRange(0, 2));
        return;
      }

      const periodSuffix = input.value.slice(numericEnd);
      const parts = numericPart.split(':');
      let segIdx = 0;
      let acc = 0;
      for (let i = 0; i < parts.length; i++) {
        acc += parts[i].length + (i > 0 ? 1 : 0);
        if (effectivePos <= acc) {
          segIdx = i;
          break;
        }
        segIdx = i;
      }

      const lastNumericChar = numericPart.trimEnd().slice(-1);
      if (periodSuffix.trim().length > 0 && /\d/.test(lastNumericChar) && pos >= numericEnd && hourCycle === 12) {
        const newSuffix = periodSuffix.trim() === 'AM' ? ' PM' : ' AM';
        onChange(numericPart + newSuffix);
        requestAnimationFrame(() => input.setSelectionRange(numericEnd + 1, input.value.length));
        return;
      }

      const maxVals = [hourCycle === 12 ? 12 : 23, 59, 59];
      const minVals = [hourCycle === 12 ? 1 : 0, 0, 0];
      const cur = parseInt(parts[segIdx] ?? '0', 10);
      const max = maxVals[segIdx];
      const min = minVals[segIdx];
      let next = isNaN(cur) ? (delta > 0 ? min : max) : cur + delta;
      if (next > max) next = min;
      if (next < min) next = max;
      parts[segIdx] = String(next).padStart(2, '0');
      const newNumeric = parts.join(':');
      onChange(newNumeric + periodSuffix);
      requestAnimationFrame(() => {
        const colonsBefore = parts.slice(0, segIdx).reduce((a, p) => a + p.length + 1, 0);
        input.setSelectionRange(colonsBefore, colonsBefore + parts[segIdx].length);
      });
      return;
    }
    // Any other key clears segment-controlled mode
    setSegmentControlled(false);

    // a/p toggles AM/PM in 12-hour mode; m is consumed since AM/PM is set by a/p
    if (hourCycle === 12 && /^[aApPmM]$/.test(e.key)) {
      e.preventDefault();
      if (e.key === 'a' || e.key === 'A') {
        onChange(`${numericPart} AM`);
      } else if (e.key === 'p' || e.key === 'P') {
        onChange(`${numericPart} PM`);
      }
      // m/M is a no-op — AM/PM suffix is already complete from a/p press
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      className={`min-w-0 w-20 bg-transparent outline-none tabular-nums placeholder:text-neutral-alpha-7${segmentControlled ? ' selection:bg-ring-primary' : ''}`}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const cleaned = e.target.value.replace(/[^\d:aAmMpP\s]/g, '');
        onChange(cleaned);
      }}
      onFocus={onFocus}
      onMouseDown={() => setSegmentControlled(false)}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      aria-invalid={isInvalid || undefined}
      aria-required={isRequired || undefined}
    />
  );
}

export default TimeTextInput;
